import React, { useEffect, useState } from 'react';

import styled from '@emotion/styled';

import {
  formateDatimeStrToNiajaTime,
  isDateTimeString,
} from '../../utils/utils';
import { Button } from '../Button/PrimaryButton';
import CheveronLeft from '../Icons/CheveronLeft';
import CheveronRight from '../Icons/CheveronRight';
import { EmptyTableComponent } from './EmptyTableComponent';
import UpdatedDateComponent from './TableCustomCellComponents/UpdatedDateComponent';
import { TableDetails } from './TableDetails';
import { TableLoadingSkeleton } from './TableLoadingSkeleton';

interface ImappingData {
  [key: string]: string;
}

interface ITable {
  showTableLoadingIcon: boolean;
  setShowTableLoadingIcon: (...arg: any) => void;
  page: number;
  endPage: number;
  mappingData?: ImappingData;
  tData: object[];
  tableHeader: string[];
  clickLeft: (...arg: any) => void;
  clickRight: (...arg: any) => void;
  cellNavigation?: (...arg: any[]) => void;
  rigthRevealComponent?: (...args: any[]) => React.ReactNode;
}

export const Table: React.FC<ITable> = ({
  showTableLoadingIcon,
  setShowTableLoadingIcon,
  page,
  endPage,
  mappingData,
  tData,
  clickLeft,
  clickRight,
  tableHeader,
  cellNavigation,
  rigthRevealComponent,
}) => {
  const [tableHeaderLabels, setTableHeaderLabels] = useState<string[]>([]);
  const [tableData, setTableData] = useState<ITable | any>([]);
  const [showTableDetails, setShowTableDetails] = useState(false);
  const [tableDetailsData, setTableDetailsData] = useState<any>();
  const [tableCellCursorIcon, setTableCellCursorIcon] = useState(false);

  useEffect(() => {
    setTableHeaderLabels(tableHeader);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setTableData(tData);
    // setTableData(TableData);
  }, [tData]);

  useEffect(() => {
    if (cellNavigation || rigthRevealComponent) {
      setTableCellCursorIcon(true);
    }
  }, [setTableCellCursorIcon, cellNavigation, rigthRevealComponent]);

  const onClickLeft = () => {
    clickLeft();
    setShowTableLoadingIcon(true);
  };

  const onClickRight = () => {
    clickRight();
    setShowTableLoadingIcon(true);
  };

  const config = tableHeaderLabels.map((tabelLabel) => {
    return { label: tabelLabel, render: (data: any) => data[tabelLabel] };
  });

  const renderedHeaders = config.map((column: any) => {
    const label = mappingData ? mappingData[column.label] : column.label;

    return (
      <th scope="col" key={column.label}>
        {label}
      </th>
    );
  });

  const renderedRows = tableData.map((data: any) => {
    const renderedCells = config.map((column: any) => {
      if (column.label === 'warehouseRole') {
        return (
          <td key={column.label}>
            {column.render(data) === 'WAREHOUSE_ADMIN' ? (
              <div>SuperAdmin</div>
            ) : (
              <div>Admin</div>
            )}
          </td>
        );
      }

      if (
        column.label === 'lastUpdatedDate' &&
        mappingData?.lastUpdatedDate === 'Updated Date'
      ) {
        return (
          <td key={column.label}>
            <UpdatedDateComponent tModalData={data}>
              {column.render(data)}
            </UpdatedDateComponent>
          </td>
        );
      }

      if (column.render(data) === null) {
        return (
          <td key={column.label}>
            <InActiveDiv>N/A</InActiveDiv>
          </td>
        );
      }

      if (typeof column.render(data) === 'boolean') {
        return (
          <td key={column.label}>
            {column.render(data) ? (
              <ActiveDiv>Active</ActiveDiv>
            ) : (
              <InActiveDiv>Inactive</InActiveDiv>
            )}
          </td>
        );
      } else if (isDateTimeString(column.render(data))) {
        return (
          <td key={column.label}>
            {formateDatimeStrToNiajaTime(column.render(data))}
          </td>
        );
      } else {
        return <td key={column.label}>{column.render(data)}</td>;
      }
    });

    return (
      <tr
        className={tableCellCursorIcon ? 'cursorIcon' : ''}
        key={JSON.stringify(data)}
        onClick={(e) => {
          cellNavigation && cellNavigation(data);
          rigthRevealComponent && setShowTableDetails(true);
          rigthRevealComponent && setTableDetailsData(data);
        }}
      >
        {renderedCells}
      </tr>
    );
  });

  return (
    <>
      <StyledTable className={tableData[0] ? '' : 'emptyTable'}>
        <thead>
          <tr>{renderedHeaders}</tr>
        </thead>
        {showTableLoadingIcon ? (
          ''
        ) : (
          <tbody>{tableData[0] ? renderedRows : null}</tbody>
        )}
      </StyledTable>
      {showTableLoadingIcon ? <TableLoadingSkeleton /> : ''}
      {tableData[0] ? (
        <TablePaginatorArea>
          {`${page} of ${endPage}`}
          <TablePaginatorBtnCtrl>
            <Button disabled={page === 1 ? true : false} onClick={onClickLeft}>
              <CheveronLeft />
            </Button>
            <Button
              disabled={page === endPage ? true : false}
              onClick={onClickRight}
            >
              <CheveronRight />
            </Button>
          </TablePaginatorBtnCtrl>
        </TablePaginatorArea>
      ) : null}

      {tableData[0] || showTableLoadingIcon ? null : (
        <NoDataStatus>
          <EmptyTableComponent link="/orders">
            Book a dispatch
          </EmptyTableComponent>
        </NoDataStatus>
      )}

      {/* Table right slider */}
      {rigthRevealComponent && (
        <TableDetails
          tableDetailsData={tableDetailsData}
          showTableDetails={showTableDetails}
          setShowTableDetails={setShowTableDetails}
        >
          {rigthRevealComponent()}
        </TableDetails>
      )}
    </>
  );
};

// ---

const TablePaginatorArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
`;

const TablePaginatorBtnCtrl = styled.div`
  display: flex;
`;
// ---

const NoDataStatus = styled.div`
  display: grid;
  place-content: center;
  margin-top: 5rem;
  margin-bottom: 5rem;
`;

const StyledTable = styled.table`
  font-size: 0.87rem;
  font-weight: 400;
  line-height: 2em;
  color: ${({ theme }) => theme.palette.secondaryText};
  background-color: white;
  border-collapse: collapse;
  border-radius: 0.5rem;
  overflow: hidden;
  outline: 1px solid ${({ theme }) => theme.palette.brandGrey};

  &.emptyTable {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  @media screen and (max-width: 820px) {
    width: 45rem;
  }

  & thead {
    text-align: left;
    font-weight: 700;
    background-color: ${({ theme }) => theme.palette.brandTableHeader};
  }

  & th,
  & td {
    padding: 1rem;
  }

  & tbody tr {
    /* cursor: pointer; */
    border-bottom: 1px solid ${({ theme }) => theme.palette.brandGrey};
  }

  & tbody tr.cursorIcon {
    cursor: pointer;
  }

  & tbody tr:last-child {
    border-bottom: 0px solid transparent;
  }

  & tbody tr:hover {
  }
`;

const ActiveDiv = styled.div`
  color: #22b231;
  background-color: #ebf7ee;
  text-align: center;
  border-radius: 0.5rem;
  padding: 0.25rem 0.5rem;
  width: fit-content;
  text-transform: capitalize;
  font-size: 0.875rem;
  /* align-self: center; */
  /* margin: auto; */
`;

const InActiveDiv = styled.div`
  color: #a9a9a9;
  background-color: #f5f5f5;
  text-align: center;
  border-radius: 0.5rem;
  padding: 0.25rem 0.5rem;
  width: fit-content;
  text-transform: capitalize;
  font-size: 0.875rem;
  /* align-self: center; */
  /* margin: auto; */
`;
